"use client";

import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import {
  getAttendanceStreak,
  type AttendanceStreakResult,
} from "@/lib/server-actions/attendanceRecordActions";
import { cn } from "@/lib/utils";

/**
 * 대시보드 "연속 학습일" 위젯 (JCG-2073).
 * 활성 회차 기준 연속 출석일(streak) + 최근 7일 출석 스트립(getAttendanceStreak, JCG-2070).
 */
export default function DashboardStreak({
  className,
}: {
  className?: string;
}) {
  const [data, setData] = useState<AttendanceStreakResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getAttendanceStreak();
        if (alive) setData(res);
      } catch (e) {
        console.error("대시보드 연속 학습일 로드 오류:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <DashboardCard
      icon={Flame}
      title="연속 학습일"
      href="/my/study-progress"
      linkLabel="진행상황"
      className={className}
    >
      {loading ? (
        <DashboardCardLoading height={88} label="연속 학습일 불러오는 중…" />
      ) : data ? (
        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-extrabold text-amber-600 tabular-nums dark:text-amber-400">
              {data.streak}
            </span>
            <span className="text-sm text-muted-foreground">일 연속</span>
            {data.attendedToday && (
              <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-950 dark:text-amber-400">
                오늘 출석
              </span>
            )}
          </div>
          <div className="mt-3 flex justify-between gap-1">
            {data.last7.map((d) => (
              <div key={d.date} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className={cn(
                    "h-6 w-full rounded-md",
                    d.attended ? "bg-amber-500" : "bg-muted",
                    d.isToday && "ring-2 ring-amber-300 dark:ring-amber-700",
                  )}
                  title={`${d.date}${d.attended ? " 출석" : ""}`}
                />
                <span className="text-[10px] text-muted-foreground">{d.weekday}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </DashboardCard>
  );
}
